import { Avatar, Box, Stack, Typography } from '@mui/material';
import SportsRugbyIcon from '@mui/icons-material/SportsRugby';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { getTotalTimer } from '../live/sports/rules.js';

const DISCIPLINE_LABELS = {
  rugby: 'RUGBY',
  soccer: 'FOOTBALL',
};

const FORCE_ENDED_AFTER_MS = 24 * 60 * 60 * 1000;
const NEUTRAL_MATCH_STATUSES = new Set([
  '',
  'scheduled',
  'not_started',
  'notStarted',
  'pending',
]);

const hasValidScore = (match) =>
  typeof match?.homeScore === 'number' &&
  typeof match?.awayScore === 'number';

const getScoreLabel = (match) => {
  if (!hasValidScore(match)) {
    return match?.statusLabel || '-';
  }

  return `${match.homeScore} - ${match.awayScore}`;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatTimeLabel = (value) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const getMatchTimestamp = (match) => {
  const parsed = new Date(match?.dateTime || match?.time || 0);
  const timestamp = parsed.getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const normalizeUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/') || url.startsWith('http')) return url;
  return `/${url}`;
};

const isMatchStarted = (match) =>
  toNumber(match?.timer) !== 0 ||
  toNumber(match?.period) !== 0 ||
  match?.statusTimer === 'on';

const hasBlockingStatus = (match) => {
  const status = String(match?.status || '').trim();
  return Boolean(status) && !NEUTRAL_MATCH_STATUSES.has(status);
};

const isMatchPastGracePeriod = (match) => {
  const timestamp = getMatchTimestamp(match);

  if (!Number.isFinite(timestamp) || timestamp === Number.MAX_SAFE_INTEGER) {
    return false;
  }

  return Date.now() - timestamp >= FORCE_ENDED_AFTER_MS;
};

const isMatchEnded = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);
  const statusTimer = match?.statusTimer;

  if (match?.status === 'end') {
    return true;
  }

  const isNativeEnded =
    ['soccer', 'rugby'].includes(discipline) &&
    timer === 0 &&
    statusTimer === 'off' &&
    [2, 6].includes(period);

  if (isNativeEnded) {
    return true;
  }

  return !hasBlockingStatus(match) && isMatchPastGracePeriod(match);
};

const isMatchReported = (match) => match?.status === 'reported';

const isHalfTime = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);

  return (
    ['soccer', 'rugby'].includes(discipline) &&
    match?.statusTimer === 'off' &&
    timer === 0 &&
    [1, 4].includes(period)
  );
};

const isEndRegulationTime = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);

  return (
    ['soccer', 'rugby'].includes(discipline) &&
    match?.statusTimer === 'off' &&
    timer === 0 &&
    period === 3
  );
};

const isPenaltyShootout = (match) =>
  ['soccer', 'rugby'].includes(match?.discipline) &&
  toNumber(match?.period) === 5;

const getMinuteLabel = (match) => {
  const period = toNumber(match?.period);
  const timer = Math.max(0, toNumber(match?.timer));
  const discipline = match?.discipline;

  const totalTimer = getTotalTimer(period, timer, discipline);
  const normalizedTotalTimer = Number.isFinite(Number(totalTimer))
    ? Number(totalTimer)
    : timer;

  const minutes = Math.floor(normalizedTotalTimer / 60);

  return `${minutes + 1}'`;
};

const getStatusLabel = (match) => {
  if (isMatchReported(match)) return 'Reporté';
  if (isMatchEnded(match)) return 'Fin du match';
  if (isPenaltyShootout(match)) return 't.a.b';
  if (isHalfTime(match)) return 'Mi-temps';
  if (isEndRegulationTime(match)) return 'Fin du temps réglementaire';
  if (isMatchStarted(match)) return getMinuteLabel(match);
  return formatTimeLabel(match?.dateTime || match?.time);
};

const getStatusColor = (match) => {
  if (!isMatchStarted(match)) return 'text.secondary';
  if (isMatchEnded(match)) return 'text.secondary';
  if (match?.statusTimer === 'on') return '#0f9f6e';
  return '#b7791f';
};

export default function DirectCalendarSection({
  group,
  showDisciplineTitle = true,
  onNavigate,
}) {
  const competitions = Array.isArray(group?.competitions)
    ? group.competitions
    : [];

  const discipline = group?.discipline;

  return (
    <Box>
      {showDisciplineTitle ? (
        <Typography
          variant="h5"
          sx={{ color: '#374365', fontWeight: 500, mb: 2 }}
        >
          {DISCIPLINE_LABELS[discipline] || group?.label || discipline}
        </Typography>
      ) : null}

      <Stack spacing={3}>
        {competitions.map((competition) => {
          const matches = Array.isArray(competition.matches)
            ? [...competition.matches].sort(
                (a, b) => getMatchTimestamp(a) - getMatchTimestamp(b)
              )
            : [];

          return (
            <Box key={competition.competitionId || competition.competitionName}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                {competition.competitionName}
                {competition.season ? ` - ${competition.season}` : ''}
              </Typography>

              {competition.roundLabel ? (
                <Typography sx={{ mb: 2 }}>
                  {competition.roundLabel}
                </Typography>
              ) : null}

              <Box
                sx={{
                  py: 2,
                  textAlign: 'center',
                  fontStyle: 'italic',
                  fontWeight: 700,
                  color: '#666',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              >
                Cliquer sur un match pour accéder au live
              </Box>

              {matches.map((match) => {
                const normalizedMatch = {
                  ...match,
                  discipline: match.discipline || discipline,
                };
                const statusLabel = getStatusLabel(normalizedMatch);
                const statusColor = getStatusColor(normalizedMatch);

                return (
                  <Box
                    key={match._id || match.idMatch || match.id}
                    onClick={() => {
                      const url = normalizeUrl(match.liveUrl);
                      if (url) onNavigate?.(url);
                    }}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr 36px 82px 36px 1fr',
                        md: '1fr 56px 120px 56px 1fr',
                      },
                      alignItems: 'center',
                      minHeight: 72,
                      borderBottom: '1px solid #ddd',
                      cursor: match.liveUrl ? 'pointer' : 'default',
                      px: { xs: 1, md: 2 },
                      columnGap: { xs: 0.5, md: 1 },
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: 'right',
                        fontWeight: match.homeWinner ? 800 : 400,
                        fontSize: { xs: 13, md: 16 },
                        lineHeight: 1.2,
                      }}
                    >
                      {match.homeTeam?.name || match.homeName}
                    </Typography>

                    <TeamLogo team={match.homeTeam} discipline={discipline} />

                    <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                      {statusLabel ? (
                        <Typography
                          sx={{
                            fontSize: 11,
                            lineHeight: 1,
                            color: statusColor,
                            mb: 0.5,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {statusLabel}
                        </Typography>
                      ) : null}

                      <Typography
                        sx={{
                          textAlign: 'center',
                          fontWeight: 900,
                          fontSize: { xs: 16, md: 18 },
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getScoreLabel(match)}
                      </Typography>
                    </Box>

                    <TeamLogo team={match.awayTeam} discipline={discipline} />

                    <Typography
                      sx={{
                        textAlign: 'left',
                        fontWeight: match.awayWinner ? 800 : 400,
                        fontSize: { xs: 13, md: 16 },
                        lineHeight: 1.2,
                      }}
                    >
                      {match.awayTeam?.name || match.awayName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function TeamLogo({ team, discipline }) {
  return (
    <Avatar
      src={team?.logoUrl || ''}
      alt={team?.name || ''}
      sx={{
        width: { xs: 28, md: 34 },
        height: { xs: 28, md: 34 },
        mx: 'auto',
        bgcolor: 'grey.200',
        color: 'text.secondary',
      }}
      imgProps={{
        style: {
          objectFit: 'contain',
        },
      }}
    >
      {discipline === 'soccer' ? (
        <SportsSoccerIcon fontSize="small" />
      ) : (
        <SportsRugbyIcon fontSize="small" />
      )}
    </Avatar>
  );
}